import React, { useEffect, useState } from "react";
import {
    ContentGetItemResponse,
    IOptions,
    ParamsGetItem,
    ParamsGetSubKategori,
    SelectedProductFaktur,
} from "../../../constants/types";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useItem from "../../../services/queries/useItem";
import useSubKategori from "../../../services/queries/useSubKategori";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import toRupiah from "../../../utils/toRupiah";
import IconButton from "@mui/material/IconButton";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useAuth } from "../../../context/authContext";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import AscDescIcon from "../../AscDescIcon/AscDescIcon";
import LoadingTabel from "../../LoadingTabel/LoadingTabel";
import NoRowsImage from "../../NoRowsImage/NoRowsImage";
import SearchIcon from "@mui/icons-material/Search";
import useUser from "../../../services/queries/useUser";
import Stack from "@mui/material/Stack";
import Spinner from "../../Spinner/Spinner";

interface IPilihBarangStepProps {
    addProducts: (item: SelectedProductFaktur[]) => void;
}

const PilihBarangStep = ({ addProducts }: IPilihBarangStepProps) => {
    const theme = useTheme();
    const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { data: user, status: statusUser } = useUser();
    const { ukmIdUser } = useAuth();

    const initialParamsStok: ParamsGetItem = {
        ukmId: ukmIdUser && ukmIdUser[0],
        search: "",
        subKategori: "",
        size: 10,
        page: 1,
    };

    const initialParamsSubKategori: ParamsGetSubKategori = {
        ukmId: ukmIdUser && ukmIdUser[0],
        search: "",
        size: 500,
        page: 1,
    };

    const [paramsStok, setParamsStok] = useState(initialParamsStok);
    const [paramsSubKategori, setParamsSubKategori] = useState(
        initialParamsSubKategori,
    );
    const [search, setSearch] = useState<string>("");
    const [selectedSubKategori, setSelectedSubKategori] =
        React.useState<IOptions | null>(null);

    const { data: stok, isLoading: isLoadingStok } = useItem(paramsStok);
    const { data: subkategori, isLoading: isLoadingSubKategori } =
        useSubKategori(paramsSubKategori);

    const [selectedProducts, setSelectedProducts] = useState<
        SelectedProductFaktur[]
    >([]);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearch = () => {
        setParamsStok((prev) => ({
            ...prev,
            search: search,
        }));
    };

    const handleOnPagination = (page: number) => {
        setParamsStok((prev) => ({
            ...prev,
            page: page + 1,
        }));
    };

    const handleOnSizeChange = (size: number) => {
        setParamsStok((prev) => ({ ...prev, size }));
    };

    const isSelected = (id: number) => {
        const filteredProduct = selectedProducts.filter(
            (li) => li.id === id,
        ).length;
        if (filteredProduct > 0) {
            return true;
        } else {
            return false;
        }
    };

    const handleSelectedProducts = (
        id: number,
        nama: string,
        sku: string,
        harga: number,
        stok: number,
        hargaBeli: number,
    ) => {
        if (isSelected(id)) {
            const newList = selectedProducts.filter((li) => li.id !== id);
            setSelectedProducts(newList);
        } else {
            setSelectedProducts((prev) => [
                ...prev,
                {
                    id,
                    nama,
                    sku,
                    harga,
                    stok,
                    hargaBeli,
                },
            ]);
        }
    };

    const columns: GridColDef[] = [
        {
            field: "namaBarang",
            headerName: "Nama Barang",
            width: 300,
            flex: 2,
            minWidth: 150,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "subKategori",
            headerName: "Sub Kategori",
            headerAlign: "center",
            align: "center",
            // width: 150,
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={params.value.nama}>
                    <span className="table-cell-trucate">
                        {params.value.nama}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "harga",
            headerName: "Harga",
            headerAlign: "center",
            align: "center",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 125,
            renderCell: (params) => (
                <Tooltip title={toRupiah(params.value ?? 0)}>
                    <span className="table-cell-trucate">
                        {toRupiah(params.value ?? 0)}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "sku",
            headerName: "SKU",
            headerAlign: "center",
            align: "center",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="table-cell-trucate">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "stok",
            headerName: "Stok",
            headerAlign: "center",
            align: "center",
            // type: "number",
            // width: 150,
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Tooltip
                    title={params.value === null ? "\u221e" : params.value}
                >
                    <span className="table-cell-trucate">
                        {params.value === null ? "\u221e" : params.value}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "aksi",
            headerName: "Aksi",
            headerAlign: "center",
            align: "center",
            minWidth: 100,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const row: ContentGetItemResponse = params.row;
                return (
                    <IconButton
                        disabled={row.stok === null}
                        onClick={() => {
                            handleSelectedProducts(
                                row.id,
                                row.namaBarang,
                                row.sku,
                                Number(row.harga),
                                row.stok,
                                row.faktur.length > 0
                                    ? Number(row.faktur[0].detail[0].hargaBeli)
                                    : 0,
                            );
                            // setSelectedProducts((prev) => [
                            //     ...prev,
                            //     {
                            //         id: params.row.id,
                            //         nama: params.row.namaBarang,
                            //         sku: params.row.sku,
                            //         harga: params.row.harga,
                            //         stok: params.row.stok,
                            //     },
                            // ]);
                        }}
                        sx={{
                            minWidth: "unset",
                            padding: "6px",
                            width: "fit-content",
                        }}
                        // startIcon={<EditIcon />}
                        size="small"
                        color="primary"
                    >
                        {isSelected(params.row.id) ? (
                            <CheckBoxIcon />
                        ) : (
                            <CheckBoxOutlineBlankIcon />
                        )}
                    </IconButton>
                );
            },
        },
    ];

    useEffect(() => {
        const timeoutId = setTimeout(
            () =>
                setParamsStok((prev) => ({
                    ...prev,
                    search: search,
                })),
            1000,
        );
        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        if (user) {
            setParamsStok((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));

            setParamsSubKategori((prev) => ({
                ...prev,
                ukmId: ukmIdUser && ukmIdUser[0],
            }));
        }
    }, [ukmIdUser, user]);

    useEffect(() => {
        if (selectedSubKategori !== null) {
            setParamsStok((prev) => ({
                ...prev,
                subKategori: String(selectedSubKategori.id),
            }));
        } else {
            setParamsStok((prev) => ({
                ...prev,
                subKategori: "",
            }));
        }
    }, [selectedSubKategori]);

    useEffect(() => {
        addProducts(selectedProducts ?? []);
    }, [addProducts, selectedProducts]);

    const isLoading = statusUser === "loading" || isLoadingSubKategori;

    if (isLoading) {
        return (
            <Stack
                display="grid"
                gridTemplateColumns="1fr"
                alignItems="center"
                height={400}
                position="relative"
            >
                <Spinner />
            </Stack>
        );
    }

    return (
        <Box>
            <Box
                display="grid"
                gridTemplateColumns={isTabletScreen ? "1fr" : "1fr 0.5fr"}
                columnGap="1em"
                rowGap={isTabletScreen ? "1em" : undefined}
                mb={2}
            >
                <TextField
                    placeholder="Cari barang..."
                    size="small"
                    value={search}
                    onChange={handleChangeSearch}
                    variant="outlined"
                    fullWidth
                    onKeyPress={(e) => {
                        const element = e.target as HTMLInputElement;
                        if (e.key === "Enter") {
                            setParamsStok((prev) => ({
                                ...prev,
                                search: element.value,
                            }));
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleSearch}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: {
                            backgroundColor: "white",
                        },
                    }}
                />
                <Autocomplete
                    disablePortal
                    loading={isLoadingSubKategori}
                    options={subkategori?.content ?? []}
                    getOptionLabel={(option) => option.nama}
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                    }
                    size="small"
                    value={selectedSubKategori}
                    onChange={(_event, newKategori: IOptions | null) => {
                        setSelectedSubKategori(newKategori);
                    }}
                    // sx={{ width: 300 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Pilih sub kategori" />
                    )}
                />
            </Box>
            <Box
                sx={{
                    height: 454,
                    width: "100%",
                    "& .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                        visibility: "visible",
                        width: "0 !important",
                    },
                }}
            >
                <DataGrid
                    density="compact"
                    rows={stok?.content ?? []}
                    columns={columns}
                    components={{
                        ColumnUnsortedIcon: AscDescIcon,
                        NoRowsOverlay: NoRowsImage,
                        LoadingOverlay: LoadingTabel,
                    }}
                    disableColumnMenu
                    disableSelectionOnClick
                    rowsPerPageOptions={[10]}
                    paginationMode="server"
                    loading={isLoadingStok || isLoadingSubKategori}
                    onPageSizeChange={handleOnSizeChange}
                    onPageChange={handleOnPagination}
                    rowCount={stok?.totalElements ?? 0}
                    page={paramsStok.page - 1}
                    pageSize={paramsStok.size}
                />
            </Box>
            <Box mt={2}>
                <Typography fontWeight="bold">Barang Terpilih:</Typography>
                <Typography>
                    {selectedProducts.map((li) => li.nama).join(", ")}
                </Typography>
            </Box>
        </Box>
    );
};

export default PilihBarangStep;
